import { Box, Button, Chip, MenuItem, Stack, TextField } from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { PageHeader } from '../../shared/components/PageHeader';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { LlmRequestDetailDialog } from './LlmRequestDetailDialog';
import { didUseWebSearch } from './promptMonitoring.utils';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { ListQuery } from '../../core/types/api';
import type { LlmRequest, Project } from '../../core/types/data-models';

type HasErrorFilter = 'all' | 'error' | 'ok';

/**
 * Read-only audit log of `LlmRequest` records. Server-side filterable by
 * project, model, and error status; a row click opens the full record in a
 * view-only dialog — there is no create/edit/delete here.
 */
export function PromptMonitoringPage() {
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [projectId, setProjectId] = useState<number | null>(null);
  const [modelInput, setModelInput] = useState('');
  const [model, setModel] = useState('');
  const [hasError, setHasError] = useState<HasErrorFilter>('all');
  const [selected, setSelected] = useState<LlmRequest | null>(null);

  // Debounce the free-text model filter so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setModel(modelInput.trim()), 400);
    return () => clearTimeout(t);
  }, [modelInput]);

  // Any filter change invalidates the current page — jump back to the first page.
  useEffect(() => {
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [projectId, model, hasError]);

  const projectsLookup = useQuery({
    queryKey: ['projects', 'select'],
    queryFn: () => resources.projects.list({ pageSize: 200, sort: 'name' }),
  });
  const projectName = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of projectsLookup.data?.data ?? []) m.set(p.id, p.name);
    return m;
  }, [projectsLookup.data]);

  const filter: ListQuery['filter'] = useMemo(() => {
    const f: NonNullable<ListQuery['filter']> = {};
    if (projectId != null) f.projectId = projectId;
    if (model) f.model = model;
    if (hasError !== 'all') f.hasError = hasError === 'error' ? 'true' : 'false';
    return f;
  }, [projectId, model, hasError]);

  const list = useQuery({
    queryKey: ['llmRequests', pagination, filter],
    queryFn: () =>
      resources.llmRequests.list({
        page: pagination.page + 1,
        pageSize: pagination.pageSize,
        sort: '-id',
        filter,
      }),
    placeholderData: (prev) => prev,
  });

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 170,
        renderCell: (p) => new Date(p.value as string).toLocaleString(),
      },
      {
        field: 'projectId',
        headerName: 'Project',
        width: 180,
        renderCell: (p) => (p.value ? projectName.get(p.value as number) ?? `#${p.value}` : '—'),
      },
      { field: 'provider', headerName: 'Provider', width: 110 },
      { field: 'model', headerName: 'Model', width: 200 },
      {
        field: 'options',
        headerName: 'Web Search',
        width: 180,
        sortable: false,
        renderCell: (p) => {
          const options = p.value as string | null;
          const modelType = p.row.modelType as string | null;
          const sourceCount = (p.row.citations as LlmRequest['citations'])?.length ?? 0;
          const webSearchUsed = didUseWebSearch(options);
          if (!modelType && !webSearchUsed) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                —
              </Box>
            );
          }
          return (
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ height: '100%' }}>
              {modelType && (
                <Chip
                  label={modelType.charAt(0).toUpperCase() + modelType.slice(1)}
                  size="small"
                  variant="outlined"
                />
              )}
              {webSearchUsed && (
                <Chip
                  icon={<TravelExploreIcon sx={{ fontSize: 16 }} />}
                  label={sourceCount > 0 ? `Used (${sourceCount})` : 'Used'}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </Stack>
          );
        },
      },
      { field: 'executionTime', headerName: 'Time (ms)', width: 110, type: 'number' },
      {
        field: 'totalTokens',
        headerName: 'Tokens',
        width: 100,
        type: 'number',
        renderCell: (p) => p.value ?? '—',
      },
      {
        field: 'error',
        headerName: 'Status',
        width: 100,
        sortable: false,
        renderCell: (p) => (
          <Chip
            label={p.value ? 'Error' : 'OK'}
            size="small"
            color={p.value ? 'error' : 'success'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'prompt',
        headerName: 'Prompt',
        width: 320,
        sortable: false,
      },
    ],
    [projectName],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title="Prompt Monitoring"
        description="Audit log of LLM requests across projects. Click a row to view the full request and response."
      />

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 260 }}>
          <EntitySelect<Project>
            label="Project"
            cacheKey={['projects', 'select']}
            service={resources.projects}
            value={projectId}
            onChange={setProjectId}
          />
        </Box>
        <TextField
          label="Model"
          placeholder="Exact model id, e.g. gpt-4o"
          value={modelInput}
          onChange={(e) => setModelInput(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          label="Status"
          value={hasError}
          onChange={(e) => setHasError(e.target.value as HasErrorFilter)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="error">Errors only</MenuItem>
          <MenuItem value="ok">No errors</MenuItem>
        </TextField>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => list.refetch()}
          disabled={list.isFetching}
        >
          Reload
        </Button>
      </Stack>

      <DataTable<LlmRequest>
        rows={list.data?.data ?? []}
        columns={columns}
        loading={list.isFetching}
        rowCount={list.data?.page.total ?? 0}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        errorMessage={list.isError ? extractErrorMessage(list.error) : undefined}
        onRowClick={(row) => setSelected(row)}
      />

      <LlmRequestDetailDialog
        open={selected !== null}
        request={selected}
        projectName={selected?.projectId ? projectName.get(selected.projectId) : undefined}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
}
